import { definePreset } from '@primeuix/themes';
import Aura from '@primeuix/themes/aura';

/**
 * Preset PrimeNG d'Abonéo — grammaire visuelle reprise de Prodicee (AMETER).
 *
 * Voir docs/redesign/GRAMMAIRE-VISUELLE.md. Base Aura (surfaces slate en clair,
 * zinc en sombre), primaire bleu, et trois écarts assumés par rapport à Prodicee
 * pour tenir le contraste AA :
 *  - bouton / lien primaire en blue-600 (et non 500) en clair ;
 *  - texte secondaire en surface-600 (et non 500) en clair ;
 *  - anneau de focus de 2 px, décalé de 2 px.
 */
export const AboneoPreset = definePreset(Aura, {
    semantic: {
        primary: {
            50: '{blue.50}',
            100: '{blue.100}',
            200: '{blue.200}',
            300: '{blue.300}',
            400: '{blue.400}',
            500: '{blue.500}',
            600: '{blue.600}',
            700: '{blue.700}',
            800: '{blue.800}',
            900: '{blue.900}',
            950: '{blue.950}',
        },
        focusRing: {
            width: '2px',
            style: 'solid',
            color: '{primary.color}',
            offset: '2px',
            shadow: 'none',
        },
        colorScheme: {
            light: {
                primary: {
                    color: '{primary.600}',
                    contrastColor: '#ffffff',
                    hoverColor: '{primary.700}',
                    activeColor: '{primary.800}',
                },
                highlight: {
                    background: '{primary.50}',
                    focusBackground: '{primary.100}',
                    color: '{primary.700}',
                    focusColor: '{primary.800}',
                },
                text: {
                    color: '{surface.700}',
                    hoverColor: '{surface.900}',
                    mutedColor: '{surface.600}',
                    hoverMutedColor: '{surface.700}',
                },
            },
            dark: {
                primary: {
                    color: '{primary.400}',
                    contrastColor: '{surface.950}',
                    hoverColor: '{primary.300}',
                    activeColor: '{primary.200}',
                },
                highlight: {
                    background: 'color-mix(in srgb, {primary.400}, transparent 84%)',
                    focusBackground: 'color-mix(in srgb, {primary.400}, transparent 76%)',
                    color: 'rgba(255,255,255,.87)',
                    focusColor: 'rgba(255,255,255,.87)',
                },
            },
        },
    },
    components: {
        // Pastilles d'état : petites majuscules grasses sur fond pastel (cf. _aboneo.scss).
        tag: {
            root: {
                fontSize: '0.75rem',
                fontWeight: '700',
                padding: '0.3rem 0.6rem',
                borderRadius: '6px',
            },
        },
        // Cartes : grand rayon, ombre diffuse.
        card: {
            root: {
                borderRadius: '14px',
                shadow: '0 3px 4px rgba(0, 0, 0, 0.1), 0 24px 36px rgba(0, 0, 0, 0.04)',
            },
            body: { padding: '1.5rem' },
            title: { fontSize: '1.285rem', fontWeight: '700' },
        },
        // Tableaux : en-têtes sans fond, lignes hautes séparées par un filet.
        datatable: {
            headerCell: {
                background: 'transparent',
                padding: '0.85rem 1rem',
                sm: { padding: '0.75rem 0.85rem' },
            },
            columnTitle: { fontWeight: '700' },
            row: { background: 'transparent' },
            bodyCell: {
                padding: '0.95rem 1rem',
                sm: { padding: '0.8rem 0.85rem' },
            },
            footer: { background: 'transparent' },
        },
        paginator: {
            root: { background: 'transparent' },
        },
        dialog: {
            root: { borderRadius: '14px' },
            title: { fontSize: '1.285rem', fontWeight: '700' },
        },
        toolbar: {
            root: { background: 'transparent', borderColor: 'transparent', padding: '0' },
        },
    },
});
